import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import KubeFleet from "../components/kube/KubeFleet";
import KubeClusterOverview from "../components/kube/KubeClusterOverview";
import KubeCosts from "../components/kube/KubeCosts";
import KubeRightSizing from "../components/kube/KubeRightSizing";
import KubeAutomation from "../components/kube/KubeAutomation";
import KubeSavings from "../components/kube/KubeSavings";
import KubeLog from "../components/kube/KubeLog";
import KubeConnect from "../components/kube/KubeConnect";
import { Loading, PlatformBadge } from "../components/kube/shared";

const TABS = [
  { id: "fleet", label: "Fleet Overview" },
  { id: "cluster", label: "Cluster Overview" },
  { id: "costs", label: "Cost Analysis" },
  { id: "rightsizing", label: "Right Sizing" },
  { id: "automation", label: "Automation & Rules" },
  { id: "savings", label: "Realized Savings" },
  { id: "log", label: "Activity Log" },
  { id: "connect", label: "Connect & Integrations" },
];

export default function ContainerOptimization() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "fleet";
  const [clusters, setClusters] = useState(null);
  const cluster = params.get("cluster") || clusters?.[0]?.id;

  useEffect(() => {
    api.kube.clusters().then((d) => setClusters(d.clusters)).catch(console.error);
  }, []);

  const go = (t, c) => setParams({ tab: t, ...(c || cluster ? { cluster: c || cluster } : {}) });

  if (!clusters) return <div className="px-6 lg:px-10 py-8"><Loading label="Loading clusters…" testId="kube-page-loading" /></div>;
  const active = clusters.find((c) => c.id === cluster) || clusters[0];

  return (
    <div data-testid="container-optimization-page">
      <div className="px-6 lg:px-10 pt-8">
        <div className="flex items-center gap-1.5 border-b border-border flex-wrap">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`kube-tab-${t.id}`} onClick={() => go(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${
                tab === t.id ? "border-[#00579e] text-[#00579e]" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "fleet" ? (
        <KubeFleet onOpenCluster={(cid) => go("cluster", cid)} />
      ) : tab === "connect" ? (
        <div className="px-6 lg:px-10 pt-6 pb-8">
          <KubeConnect clusters={clusters} />
        </div>
      ) : (
        <div className="px-6 lg:px-10 pt-5 pb-8 space-y-5">
          <div className="flex items-center gap-3 flex-wrap" data-testid="kube-cluster-picker">
            <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Cluster</span>
            <Select value={active.id} onValueChange={(v) => go(tab, v)}>
              <SelectTrigger className="h-8 w-[240px] text-xs" data-testid="kube-cluster-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {clusters.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`kube-cluster-opt-${c.name}`}>
                    {c.name} · {c.platform}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <PlatformBadge platform={active.platform} provider={active.provider} />
            <span className="cs-mono text-[11px] text-muted-foreground">
              {active.region} · k8s {active.k8s_version} · {active.nodes_managed}/{active.nodes_total} nodes · autonomy {active.autonomy}
            </span>
          </div>

          {tab === "cluster" && <KubeClusterOverview clusterId={active.id} />}
          {tab === "costs" && <KubeCosts clusterId={active.id} />}
          {tab === "rightsizing" && <KubeRightSizing clusterId={active.id} />}
          {tab === "automation" && <KubeAutomation clusterId={active.id} />}
          {tab === "savings" && <KubeSavings clusterId={active.id} />}
          {tab === "log" && <KubeLog clusterId={active.id} />}
        </div>
      )}
    </div>
  );
}
